import React, { useCallback, useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useBulkOpsSessionContext } from '@atlassian/jira-bulk-operations-analytics';
import { useEvent } from '@atlassian/jira-software-react-use-event';
import { type MenuItem, type MenuNode, MenuNodeType } from '../../../common/types';
import { ContextMenuNodeList } from '../../../common/ui/context-menu-node-list';
import { useContextMenuAnalytics } from '../../../controllers/analytics/main';
import { useContextMenuActions, useIsPersistentFocus } from '../../../controllers/context-menu';
import { ContextMenuNode } from './context-menu-node';
import { FocusLockedWrapper } from './focus-locked-wrapper';
import { hasSubmenuItems, isSubmenuOpenForNode, isSubmenuOpen } from './utils';

type ContextMenuInnerProps = {
	scope: string;
	menuItems: MenuNode[];
	openPath: string | undefined;
	shouldRenderToParent?: boolean;
};

export const ContextMenuInner = ({
	scope,
	menuItems,
	openPath,
	shouldRenderToParent = true,
}: ContextMenuInnerProps) => {
	const { closeMenu, openSubMenu, closeSubMenu, setForcedFocusPath, setPersistentFocus } =
		useContextMenuActions();
	const previousItemFocusPath = useRef<string | undefined>(undefined);
	const { addMappedAnalyticsToEvent, fireUIAnalyticsForNode } = useContextMenuAnalytics();
	const bulkOpsFlow = useBulkOpsSessionContext();
	const isPersistentFocus = useIsPersistentFocus();

	const handleOpenSubMenu = useCallback(
		(menuItem: MenuItem) => {
			previousItemFocusPath.current = menuItem.id;
			openSubMenu(scope, menuItem);
		},
		[scope, openSubMenu],
	);

	const handleCloseSubMenu = useCallback(() => {
		closeSubMenu(scope);
	}, [scope, closeSubMenu]);

	const onClickContentMenuNode = useEvent(
		(menuItem: MenuNode, analyticsEvent: UIAnalyticsEvent) => {
			if (menuItem.type === MenuNodeType.Item) {
				if (hasSubmenuItems(menuItem)) {
					if (!isSubmenuOpen(openPath)) {
						// clicked on menu item - submenu closed
						handleOpenSubMenu(menuItem);
					} else if (isSubmenuOpenForNode(menuItem, openPath)) {
						// click on opened menu item - submenu open
						handleCloseSubMenu();
					} else if (!isSubmenuOpenForNode(menuItem, openPath)) {
						// clicked on different menu item - submenu open
						handleCloseSubMenu();
						handleOpenSubMenu(menuItem);
					}
				} else {
					// clicked on root menu item
					const event = addMappedAnalyticsToEvent(analyticsEvent, menuItem.id, menuItem.analytics);
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					menuItem.onClick?.(event.clone() as UIAnalyticsEvent);
					bulkOpsFlow?.executeAction();
					const attributes = bulkOpsFlow?.getFlowAttributes();
					fireUIAnalyticsForNode(event, menuItem.id, menuItem.analytics, {
						...attributes,
						asdfsadf: 'asdfasdf',
					});
					closeMenu(scope);
				}
			}
		},
	);

	const onMouseEnter = useEvent((menuItem: MenuNode) => {
		if (isPersistentFocus) {
			setPersistentFocus(false);
		}
		if (
			!isSubmenuOpenForNode(menuItem, openPath) &&
			menuItem.type === MenuNodeType.Item &&
			hasSubmenuItems(menuItem)
		) {
			/*
              setTimeout is needed to avoid conflicting situation, when 2 updates are being kicked off
              at the same time because react-sweet-state batches updates.
             */
			setTimeout(() => {
				// hovered on menu item - submenu closed
				handleOpenSubMenu(menuItem);
			}, 0);
			return;
		}
		if (isSubmenuOpen(openPath) && !isSubmenuOpenForNode(menuItem, openPath)) {
			// We don't want to focus any element when closing a submenu because of `mouseLeave`
			previousItemFocusPath.current = undefined;
			closeSubMenu(scope);
		}
		if (menuItem.type === MenuNodeType.Item) {
			menuItem.onMouseEnter?.();
		}
	});

	const renderNode = useCallback(
		(node: MenuNode) => (
			<ContextMenuNode
				scope={scope}
				menuNode={node}
				onClick={onClickContentMenuNode}
				onMouseEnter={onMouseEnter}
				shouldRenderToParent={shouldRenderToParent}
			/>
		),
		[onClickContentMenuNode, onMouseEnter, scope, shouldRenderToParent],
	);

	useEffect(() => {
		if (!isSubmenuOpen(openPath) && previousItemFocusPath.current !== undefined) {
			setForcedFocusPath(scope, previousItemFocusPath.current);
			previousItemFocusPath.current = undefined;
		}
	}, [openPath, scope, setForcedFocusPath]);

	useEffect(() => {
		const handleEscapeNavigation = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				ev.preventDefault();
				if (isSubmenuOpen(openPath)) {
					handleCloseSubMenu();
				} else {
					closeMenu(scope);
				}
			}
		};

		window.addEventListener('keyup', handleEscapeNavigation);
		return () => {
			window.removeEventListener('keyup', handleEscapeNavigation);
		};
	}, [scope, closeMenu, handleCloseSubMenu, openPath]);

	return (
		<FocusLockedWrapper>
			<ContextMenuNodeList menuNodes={menuItems} renderNode={renderNode} />
		</FocusLockedWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const A11yList = styled.ul({
	listStyle: 'none',
	padding: '0',
	margin: '0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const A11yListItem = styled.li({
	padding: '0',
	margin: '0',
});
